import { useLayoutEffect, useRef, useState } from "react";

const STACK_ATTR = "data-sticky-stack";

function measureHeaderHeight() {
  const header = document.querySelector("header");
  return header ? header.getBoundingClientRect().height : 0;
}

/**
 * Pins its children to the top of the viewport, below the app header and
 * below any preceding `StickyStackItem` in the same DOM parent — without a
 * hand-maintained pixel offset.
 *
 * Plain CSS `position: sticky` needs each stacked element's `top` to equal
 * the sum of the heights of everything stuck above it. That sum breaks the
 * moment a sibling's content reflows (filters wrap to a second line, the
 * app header's height changes, …), so this component recomputes it from the
 * actual rendered siblings via ResizeObserver instead of a constant.
 *
 * Stacking is detected by DOM adjacency (`previousElementSibling`), not React
 * ownership — items rendered by different components still stack correctly
 * as long as they land next to each other in the DOM (true for anything
 * rendered as top-level children of the same parent, even across a
 * `<Fragment>` boundary).
 */
export default function StickyStackItem({ children, baseTop, zIndex = 30, className = "" }) {
  const ref = useRef(null);
  const [top, setTop] = useState(baseTop ?? 0);

  useLayoutEffect(() => {
    const node = ref.current;
    if (!node) return undefined;

    const stackedSiblings = () => {
      const result = [];
      let sib = node.previousElementSibling;
      while (sib) {
        if (sib.hasAttribute(STACK_ATTR)) result.push(sib);
        sib = sib.previousElementSibling;
      }
      return result;
    };

    const recompute = () => {
      const base = baseTop ?? measureHeaderHeight();
      const stackedHeight = stackedSiblings().reduce(
        (sum, sib) => sum + sib.getBoundingClientRect().height,
        0,
      );
      setTop(base + stackedHeight);
    };

    recompute();

    const ro = new ResizeObserver(recompute);
    ro.observe(node);
    stackedSiblings().forEach((sib) => ro.observe(sib));
    const header = document.querySelector("header");
    if (header) ro.observe(header);

    window.addEventListener("resize", recompute);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", recompute);
    };
  }, [baseTop]);

  return (
    <div
      ref={ref}
      data-sticky-stack=""
      className={`sticky bg-white/95 backdrop-blur ${className}`}
      style={{ top, zIndex }}
    >
      {children}
    </div>
  );
}
